import { analytics } from "@flare/analytics";
import type { User } from "@flare/next-auth";

import { meetingType } from "@/components/Funnel/funnel.consts";
import type {
  FunnelAnswers,
  FunnelMeta,
  FunnelModels,
} from "@/modules/v2/funnel/config";
import { isLeadScoreMissingInAnswersButPresentInMeta } from "@/modules/v2/funnel/helpers";
import type { SessionContext } from "@/types/next-auth";

import { BiEventsNames } from "./bi-event-names";

export function reportWebCalendarSuccess(
  user: User,
  context: SessionContext,
  funnelMeta: FunnelMeta,
) {
  const selectedMeetingType =
    context.funnelData?.eventStartTime === "call_me_now"
      ? meetingType.cmn
      : meetingType.intake;

  analytics.track(BiEventsNames.WebCalendarSuccess, {
    slot: context.funnelData?.eventStartTime,
    email_hash: funnelMeta?.hashedEmail,
    phone_hash: funnelMeta?.hashedPhone,
    email_hash_unsalted: funnelMeta?.unsaltedSanitizedEmail,
    phone_hash_unsalted: funnelMeta?.unsaltedSanitizedPhone,
    meeting_type: selectedMeetingType,
    sf_funnel_action_id: user.integrationInfo?.sfLastWFAId,
  });
}

export function getWebLeadCreateProperties(
  context: SessionContext,
  funnelMeta: FunnelMeta,
) {
  const modelsMap = funnelMeta?.models || ({} as FunnelModels);
  const models = Object.values(modelsMap);

  return {
    practice: context.funnelData?.practice,
    email_hash: funnelMeta?.hashedEmail,
    phone_hash: funnelMeta?.hashedPhone,
    email_hash_unsalted: funnelMeta?.unsaltedSanitizedEmail,
    phone_hash_unsalted: funnelMeta?.unsaltedSanitizedPhone,
    models,
  };
}

export function reportWebLeadCreate(
  user: User,
  context: SessionContext,
  funnelMeta: FunnelMeta,
) {
  const webLeadCreateProperties = getWebLeadCreateProperties(
    context,
    funnelMeta,
  );

  analytics.track(BiEventsNames.WebLeadCreate, {
    ...webLeadCreateProperties,
    sf_funnel_action_id: user.integrationInfo?.sfLastWFAId,
  });
}

export function reportMarketingWebLeadCreate(
  funnelAnswers: FunnelAnswers,
  funnelMeta: FunnelMeta,
) {
  const modelsMap = funnelMeta?.models || ({} as FunnelModels);
  const models = Object.values(modelsMap);

  analytics.track(BiEventsNames.WebMarketingLeadCreate, {
    practice: funnelAnswers?.practice,
    email_hash: funnelMeta?.hashedEmail,
    phone_hash: funnelMeta?.hashedPhone,
    email_hash_unsalted: funnelMeta?.unsaltedSanitizedEmail,
    phone_hash_unsalted: funnelMeta?.unsaltedSanitizedPhone,
    sf_funnel_action_id: null,
    models,
  });
}

export async function getAnonymousId() {
  return await analytics.getAnonymousId();
}

export function checkIsTestUser(email?: string) {
  if (!email) return null;

  return email.match(
    /(themis-tech.io|helloflare.com|marble.co|themarbleway.com|test.com)/i,
  )
    ? "test"
    : "real";
}

// It's only for debugging purposes. It should be removed after the issue is fixed.
// See: https://helloflare.atlassian.net/browse/F20-1403
export function reportLeadScoreMissingInAnswersButPresentInMeta(
  funnelAnswers: FunnelAnswers,
  funnelMeta: FunnelMeta,
) {
  const isMissing = isLeadScoreMissingInAnswersButPresentInMeta(
    funnelAnswers,
    funnelMeta,
  );

  if (!isMissing) {
    return;
  }

  analytics.track(BiEventsNames.WebFunnelError, {
    error_type: "lead-score-missing-in-answers-but-present-in-meta",
    error_message: "no lead score",
    funnelAnswers,
    funnelMeta,
  });
}
